/**
 * Utilities for formatting keybinding hints in the UI.
 */

import { getKeybindings, type Keybinding, type KeyId, matchesKey } from "@kolisachint/hoocode-tui";
import { type AppKeybinding, KEYBINDINGS } from "../../../core/keybindings.js";
import { theme } from "../theme/theme.js";

export interface KeyTextFormatOptions {
	capitalize?: boolean;
}

function formatKeyPart(part: string, options: KeyTextFormatOptions): string {
	const displayPart = process.platform === "darwin" && part.toLowerCase() === "alt" ? "option" : part;
	return options.capitalize ? displayPart.charAt(0).toUpperCase() + displayPart.slice(1) : displayPart;
}

export function formatKeyText(key: string, options: KeyTextFormatOptions = {}): string {
	return key
		.split("/")
		.map((k) => {
			const parts = k.split("+");
			// A modifier-less single character is printed exactly as it is typed.
			// Capitalising it would name a different key: in this map an uppercase
			// letter *is* shift+letter, and a bare `shift+<letter>` is banned
			// outright because a legacy terminal cannot tell it from typing. So a
			// hint reading "N" for a binding on `n` documents a chord that does not
			// exist. Inside a chord the letter is unambiguous — nobody reads
			// "Ctrl+C" as ctrl+shift+c — so that keeps its capital.
			if (parts.length === 1 && parts[0].length === 1) return parts[0];
			return parts.map((part) => formatKeyPart(part, options)).join("+");
		})
		.join("/");
}

function formatKeys(keys: KeyId[], options: KeyTextFormatOptions = {}): string {
	if (keys.length === 0) return "";
	return formatKeyText(keys.join("/"), options);
}

export function keyText(keybinding: Keybinding): string {
	return formatKeys(getKeybindings().getKeys(keybinding));
}

export function keyDisplayText(keybinding: Keybinding): string {
	return formatKeys(getKeybindings().getKeys(keybinding), { capitalize: true });
}

/**
 * The taught key for a binding — the first one — and nothing else.
 *
 * `keyDisplayText` names every key an action answers to, which is right for a
 * reference row and wrong wherever the shape of the hint is the thing being
 * taught: the dials read `alt+t/shift+alt+t`, and a second forward key spliced
 * into the middle of that is what the one-shape rule exists to avoid.
 */
export function keyDisplayLabel(keybinding: Keybinding): string {
	const keys = getKeybindings().getKeys(keybinding);
	return keys.length > 0 ? formatKeyText(keys[0] as string, { capitalize: true }) : "";
}

export function keyHint(keybinding: Keybinding, description: string): string {
	return theme.fg("dim", keyText(keybinding)) + theme.fg("muted", ` ${description}`);
}

export function rawKeyHint(key: string, description: string): string {
	return theme.fg("dim", formatKeyText(key)) + theme.fg("muted", ` ${description}`);
}

/** Default keys of an app-level binding, from its definition. */
function appDefaultKeys(id: AppKeybinding): KeyId[] {
	const defaults = KEYBINDINGS[id].defaultKeys;
	return Array.isArray(defaults) ? defaults : [defaults];
}

/**
 * Match input against an app-level keybinding. The global manager is the
 * app-aware one in normal runs (user overrides apply); when it is the bare TUI
 * default (tests, early boot) the app definition's default keys are used so
 * the component never goes dead.
 */
export function matchesAppKey(data: string, id: AppKeybinding): boolean {
	const keybindings = getKeybindings();
	if (keybindings.getDefinition(id)) return keybindings.matches(data, id);
	return appDefaultKeys(id).some((key) => matchesKey(data, key));
}

/** First configured key for an app-level binding, for hint lines. */
export function appKeyLabel(id: AppKeybinding): string {
	const keybindings = getKeybindings();
	const keys = keybindings.getDefinition(id) ? keybindings.getKeys(id) : undefined;
	if (keys && keys.length > 0) return keys[0] as string;
	return (appDefaultKeys(id)[0] ?? "") as string;
}
